// GoalsProjectsStep — Step 2 of WeeklyReviewModal.
// Read-only list of active goals + active projects with inline next-action edit.
import { useState } from "react";
import { ProgressBar } from "./bento-ui";
import { deriveProjectProgress } from "./rollup-derive";
import type { LifeOsState, Project, Goal } from "../types";

interface Props {
  state: LifeOsState;
  onUpdateNextAction: (projectId: string, nextAction: string) => void;
}

export function GoalsProjectsStep({ state, onUpdateNextAction }: Props) {
  const activeGoals = state.goals.filter((g) => g.status === "active");
  const activeProjects = state.projects.filter((p) => p.status === "active");
  const [editing, setEditing] = useState<Record<string, string>>({});

  function commitNextAction(p: Project) {
    const val = editing[p.id] ?? p.nextAction;
    onUpdateNextAction(p.id, val);
    setEditing((prev) => {
      const next = { ...prev };
      delete next[p.id];
      return next;
    });
  }

  return (
    <div className="space-y-4">
      {activeGoals.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">
            Mục tiêu đang theo đuổi ({activeGoals.length})
          </p>
          <div className="space-y-2">
            {activeGoals.map((g: Goal) => (
              <div key={g.id} className="rounded-2xl bg-violet-50 px-3 py-2.5">
                <p className="text-[13px] font-semibold text-ink-800">{g.title}</p>
                <p className="text-[11px] text-ink-500 mt-0.5">
                  {g.metric} · {g.current}/{g.target}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeProjects.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">
            Dự án đang chạy ({activeProjects.length})
          </p>
          <div className="max-h-52 overflow-y-auto space-y-2 pr-1">
            {activeProjects.map((p: Project) => {
              const progress = deriveProjectProgress(state, p.id);
              const isEditing = editing[p.id] !== undefined;
              return (
                <div key={p.id} className="rounded-2xl bg-ink-50 px-3 py-2.5 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-ink-800 truncate">{p.name}</p>
                    <span className="text-[11px] font-bold text-ink-500 shrink-0">{progress}%</span>
                  </div>
                  <ProgressBar percent={progress} />
                  {isEditing ? (
                    <div className="flex gap-1.5 mt-1">
                      <input
                        autoFocus
                        value={editing[p.id]}
                        onChange={(e) =>
                          setEditing((prev) => ({ ...prev, [p.id]: e.target.value }))
                        }
                        className="flex-1 rounded-xl border border-ink-200 bg-white px-2 py-1 text-[12px] outline-none focus:border-brand-400"
                        placeholder="Bước tiếp theo…"
                      />
                      <button
                        onClick={() => commitNextAction(p)}
                        className="rounded-xl bg-brand-500 px-3 py-1 text-[12px] font-bold text-white hover:bg-brand-600"
                      >
                        Lưu
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        setEditing((prev) => ({ ...prev, [p.id]: p.nextAction }))
                      }
                      className="text-left text-[12px] text-ink-500 hover:text-brand-600 truncate w-full"
                    >
                      {p.nextAction ? `→ ${p.nextAction}` : "+ Thêm bước tiếp theo"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeGoals.length === 0 && activeProjects.length === 0 && (
        <p className="py-6 text-center text-[13px] text-ink-400">
          Không có mục tiêu / dự án đang hoạt động.
        </p>
      )}
    </div>
  );
}
